import * as angular from "angular";
import template from "./json-schema-form.html";
import "./json-schema-form.less";
import schemaMarkdown from "./schema-markdown";
import { getSchemaUiConfigurationErrors } from "./schemaUiMessages";
import {
	JsonSchemaDefinition,
	SchemaFormDefinition,
	buildSchemaForm,
	buildSchemaFormSchema,
	normalizeSchemaFormModel
} from "./schemaFormModel";
import {
	JsonSchemaValidationResult,
	validateJsonSchemaModel
} from "./schemaValidation";

export type SchemaFormChangeHandler = (
	modelValue: unknown,
	form: SchemaFormDefinition
) => void;

export interface JsonSchemaFormOptions {
	pristine?: {
		errors?: boolean;
		success?: boolean;
		[key: string]: unknown;
	};
	formDefaults?: {
		feedback?: boolean;
		onChange?: SchemaFormChangeHandler;
		[key: string]: unknown;
	};
	[key: string]: unknown;
}

export interface JsonSchemaFormController {
	schema: JsonSchemaDefinition;
	options?: JsonSchemaFormOptions;
	hideFieldTitle?: string;
	validationResult?: JsonSchemaValidationResult;
	ngModel: angular.INgModelController;
}

// AngularJS owns these merged binding and required-controller properties.
// eslint-disable-next-line no-redeclare
export class JsonSchemaFormController {
	public form!: SchemaFormDefinition[];
	public formModel: Record<string, unknown> = {};
	public formOptions!: JsonSchemaFormOptions;
	public formSchema!: JsonSchemaDefinition;
	public showValidationSummary = false;
	public configurationErrors: string[] = [];

	private callerOnChange?: SchemaFormChangeHandler;
	private isDestroyed = false;
	private pendingModelPublication?: angular.IPromise<void>;
	private readonly removeListeners: Array<() => void>;

	public static $inject = ["$scope", "$timeout"];

	public constructor(
		private readonly $scope: angular.IScope,
		private readonly $timeout: angular.ITimeoutService
	) {
		this.removeListeners = [
			this.$scope.$on(
				"sf-render-finished",
				() => this.scheduleModelPublication()
			),
			this.$scope.$on(
				"schemaFormValidate",
				() => this.showValidationErrors()
			)
		];
	}

	public $onInit(): void {
		if(!this.schema) {
			throw new Error("jsonSchemaForm requires a schema at initialization.");
		}

		this.ngModel.$render = () => this.renderModelValue();
		this.configurationErrors = getSchemaUiConfigurationErrors(this.schema);
		this.formSchema = buildSchemaFormSchema(this.schema);
		this.form = buildSchemaForm(this.schema);
		this.hideConfiguredFieldTitle();
		this.initializeOptions();
		this.renderModelValue();
	}

	public $onDestroy(): void {
		this.isDestroyed = true;
		this.removeListeners.forEach(removeListener => removeListener());
		if(this.pendingModelPublication) {
			this.$timeout.cancel(this.pendingModelPublication);
			this.pendingModelPublication = undefined;
		}
	}

	private readonly handleFormChange = (
		modelValue: unknown,
		form: SchemaFormDefinition
	): void => {
		this.scheduleModelPublication();
		this.callerOnChange?.(modelValue, form);
	};

	private scheduleModelPublication(): void {
		if(this.isDestroyed) {
			return;
		}

		this.updateSchemaValidity(this.formModel);
		if(this.pendingModelPublication) {
			return;
		}

		this.pendingModelPublication = this.$timeout(() => {
			this.pendingModelPublication = undefined;
			if(!this.isDestroyed) {
				this.publishModelValue();
			}
		});
	}

	private publishModelValue(): void {
		const modelValue = normalizeSchemaFormModel(
			angular.copy(this.formModel),
			this.schema
		);
		this.updateSchemaValidity(modelValue);
		if(!angular.equals(modelValue, this.ngModel.$viewValue)) {
			this.ngModel.$setViewValue(modelValue);
		}
	}

	private initializeOptions(): void {
		const options = angular.copy(this.options ?? {});
		const formDefaults = {
			feedback: false,
			...(options.formDefaults ?? {})
		};

		this.callerOnChange = formDefaults.onChange;
		formDefaults.onChange = this.handleFormChange;
		this.formOptions = {
			...options,
			pristine: { errors: false, ...(options.pristine ?? {}) },
			formDefaults
		};
	}

	private hideConfiguredFieldTitle(): void {
		const visit = (definitions: SchemaFormDefinition[]) => definitions.forEach(definition => {
			if(definition.key?.length === 1 && definition.key[0] === this.hideFieldTitle) {
				definition.notitle = true;
			}
			if(definition.items) {
				visit(definition.items);
			}
		});
		visit(this.form);
	}

	private renderModelValue(): void {
		this.showValidationSummary = false;
		this.formModel = angular.copy(this.ngModel.$viewValue ?? {});
		this.scheduleModelPublication();
	}

	private showValidationErrors(): void {
		this.updateSchemaValidity(this.formModel);
		this.showValidationSummary = true;
	}

	private updateSchemaValidity(modelValue: unknown): void {
		this.validationResult = validateJsonSchemaModel(modelValue ?? {}, this.schema);
		this.ngModel.$setValidity("jsonSchema", this.validationResult.valid);
	}
}

export const jsonSchemaFormComponent: angular.IComponentOptions = {
	template,
	controller: JsonSchemaFormController,
	bindings: {
		schema: "<",
		options: "<?",
		hideFieldTitle: "<?",
		validationResult: "=?"
	},
	require: {
		ngModel: "ngModel"
	}
};

export default angular
	.module("vesl.extensions.json-schema.form", ["schemaForm", schemaMarkdown])
	.component("jsonSchemaForm", jsonSchemaFormComponent)
	.name;
